import { Injectable } from '@angular/core';
import { Auth } from '@angular/fire/auth';
import { UserService } from '../users/user.service';
import { environment } from '../../../environments/environment';
import { addDoc, collection, deleteDoc, doc, Firestore, getDoc, getDocs, serverTimestamp, updateDoc } from 'firebase/firestore';
import { TeamService } from '../teams/team.service';

@Injectable({
  providedIn: 'root'
})
export class NotificationService {

  private firestore: Firestore = environment.firestore;

  constructor(
    private auth: Auth,
    private userService: UserService,
    private teamService: TeamService
  ) { }

  // getter para o ID do utilizador autenticado
  private get userAuthId(): string | null {
    return this.auth.currentUser?.uid ?? null;
  }

  // função apra obter os membros do evento
  async getEventParticipantsExceptMe(eventId: string, userId: string): Promise<string[]> {
    const eventRef = doc(this.firestore, 'events', eventId);
    const snapshot = await getDoc(eventRef);

    if (!snapshot.exists()) return [];

    const data = snapshot.data();
    const participants: string[] = data['usersIdAccepted'] || [];

    // remover o utilizador autenticado
    return participants.filter(uid => uid !== userId);
  }

  // cria notificação de like
  async createLikeNotification(userId: string, postId: string): Promise<void> {
    if (!this.userAuthId || this.userAuthId === userId) {
      return;
    }

    try {
      const notificationRef = collection(this.firestore, 'users', userId, 'notifications');
      const docPath = `love/${postId}`;
      const userAuthName = await this.userService.fetchUserAuthName();

      const message = `${userAuthName} gostou da sua ação.`;

      await addDoc(notificationRef, {
        action: 'love',
        docPath,
        fired: false,
        message,
        pubDate: serverTimestamp(),
        read: false,
        userSenderId: this.userAuthId,
        userSenderName: userAuthName,
      });
    }
    catch (error) {
      console.error('Erro ao criar notificação:', error);
    }
  }

  // função para carregar o nome do Evento
  async fetchEventName(eventId: string): Promise<string> {
    try {
      const event = doc(this.firestore, 'events', eventId);

      const snap = await getDoc(event);

      if (!snap.exists()) {
        return '';
      }

      const data = snap.data();

      return data['name'];
    }
    catch (error) {
      return '';
    }
  }

  // função para carregar o nome da equipa
  async fetchTeamName(teamId: string): Promise<string> {
    try {
      const team = doc(this.firestore, 'teams', teamId);

      const snap = await getDoc(team);

      if (!snap.exists()) {
        return '';
      }

      const data = snap.data();

      return data['name'];
    }
    catch (error) {
      return '';
    }
  }

  // cria notificação de pedido de amizade
  async createFriendRequestNotification(userId: string) {
    try {
      const notification = collection(this.firestore, 'users', userId, 'notifications');

      const docPath = 'users/' + userId;

      const userAuthName = await this.userService.fetchUserAuthName();

      await addDoc(notification, {
        action: 'friend_request',
        docPath: docPath,
        fired: false,
        message: "Enviou um pedido de amizade.",
        pubDate: serverTimestamp(),
        read: false,
        userSenderId: this.userAuthId,
        userSenderName: userAuthName,
      });
    }
    catch (error) {
      console.error(error);
    }
  }

  // cria notificação de mensagem do chat das equipas
  async createTeamChatMessageNotification(content: string, teamId: string, userId: string) {
    try {
      const recipients = await this.teamService.getTeamParticipantsExceptMe(teamId, userId);
      if (recipients.length === 0) return;

      const userAuthName = await this.userService.fetchUserAuthName();
      const docPath = 'team_chat/' + teamId;

      const notificationPromises = recipients.map((userId: string) => {
        const notificationRef = collection(
          this.firestore,
          'users',
          userId,
          'notifications'
        );

        return addDoc(notificationRef, {
          action: 'team_chat',
          docPath: docPath,
          fired: false,
          message: content,
          pubDate: serverTimestamp(),
          read: false,
          userSenderId: userId,
          userSenderName: userAuthName,
        });
      });

      await Promise.all(notificationPromises);

    } catch (error) {
      console.error(error);
    }
  }

  // cria notificação de mensagem do chat dos eventos
  async createEventChatMessageNotification(content: string, eventId: string, userId: string) {
    try {
      const recipients = await this.getEventParticipantsExceptMe(eventId, userId);
      if (recipients.length === 0) return;

      const userAuthName = await this.userService.fetchUserAuthName();
      const docPath = 'event_chat/' + eventId;

      const notificationPromises = recipients.map((userId: string) => {
        const notificationRef = collection(
          this.firestore,
          'users',
          userId,
          'notifications'
        );

        return addDoc(notificationRef, {
          action: 'event_chat',
          docPath: docPath,
          fired: false,
          message: content,
          pubDate: serverTimestamp(),
          read: false,
          userSenderId: userId,
          userSenderName: userAuthName,
        });
      });

      await Promise.all(notificationPromises);

    } catch (error) {
      console.error(error);
    }
  }

  // cria notificação de mensagem entre dois utilizadores
  async createUserChatMessageNotification(content: string, chatId: string, userId: string) {
    try {

      if (userId === '') return;

      const userAuthName = await this.userService.fetchUserAuthName();
      const docPath = 'user_chat/' + chatId + '/' + userId;

      const notificationRef = collection(this.firestore, 'users', userId, 'notifications');

      return addDoc(notificationRef, {
        action: 'user_chat',
        docPath: docPath,
        fired: false,
        message: content,
        pubDate: serverTimestamp(),
        read: false,
        userSenderId: this.userAuthId,
        userSenderName: userAuthName,
      });

    } catch (error) {
      console.error(error);
      return null;
    }
  }

  // cria notificação de convite para participar num evento
  async createEventInviteNotification(userInvitedId: string, eventId: string, userId: string) {
    try {
      const notification = collection(this.firestore, 'users', userInvitedId, 'notifications')

      const docPath = 'events/' + eventId;
      const userAuthName = await this.userService.fetchUserAuthName();
      const eventName = await this.fetchEventName(eventId);
      const message = userAuthName + ' convidou para participar no evento ' + eventName;

      await addDoc(notification, {
        action: 'event_invitation',
        docPath: docPath,
        fired: false,
        message: message,
        pubDate: serverTimestamp(),
        read: false,
        userSenderId: userId,
        userSenderName: userAuthName,
      });
    }
    catch (error) {
      console.error(error);
    }
  }

  // cria notificação de convite para participar numa equipa
  async createTeamInviteNotification(userInvitedId: string, teamId: string, userId: string) {
    try {

      const notification = collection(this.firestore, 'users', userInvitedId, 'notifications')

      const docPath = 'teams/' + teamId;
      const userAuthName = await this.userService.fetchUserAuthName();
      const teamName = await this.fetchTeamName(teamId);
      const message = userAuthName + ' convidou para participar na equipa ' + teamName;

      await addDoc(notification, {
        action: 'team_invitation',
        docPath: docPath,
        fired: false,
        message: message,
        pubDate: serverTimestamp(),
        read: false,
        userSenderId: userId,
        userSenderName: userAuthName,
      });

      // adicionar userId ao array de convites da equipa
      const teamRef = doc(this.firestore, 'teams', teamId);
      const teamSnap = await getDoc(teamRef);

      if (teamSnap.exists()) {
        const currentInvitations = teamSnap.data()['invitations'] || [];
        if (!currentInvitations.includes(userInvitedId)) {
          await updateDoc(teamRef, {
            invitations: [...currentInvitations, userInvitedId]
          });
        }
      }
    }
    catch (error) {
      console.error(error);
    }
  }

  // cria notificação de comentário
  async createCommentNotification(userId: string, postId: string, commentId: string, message: string): Promise<void> {
    if (!this.userAuthId || this.userAuthId === userId) {
      return;
    }

    try {
      const notificationRef = collection(this.firestore, 'users', userId, 'notifications');
      const docPath = `spots/${postId}/comments/${commentId}`;
      const userAuthName = await this.userService.fetchUserAuthName();

      await addDoc(notificationRef, {
        action: 'comment',
        docPath,
        fired: false,
        message: message,
        pubDate: serverTimestamp(),
        read: false,
        userSenderId: this.userAuthId,
        userSenderName: userAuthName,
      });
    }
    catch (error) {
      console.error('Erro ao criar notificação:', error);
    }
  }

  // cria notificação de comentário
  async createTeamRequestNotification(userId: string, teamId: string): Promise<void> {
    if (!this.userAuthId) {
      return;
    }

    try {
      const notificationRef = collection(this.firestore, 'users', userId, 'notifications');
      const docPath = `teams/${teamId}`;
      const userAuthName = await this.userService.fetchUserAuthName();

      const message = 'Posso entrar para a sua equipa?'

      await addDoc(notificationRef, {
        action: 'team_request',
        docPath,
        fired: false,
        message: message,
        pubDate: serverTimestamp(),
        read: false,
        userSenderId: this.userAuthId,
        userSenderName: userAuthName,
      });
    }
    catch (error) {
      console.error('Erro ao criar notificação:', error);
    }
  }

  // apaga a notificação de convite de equipas e eventos
  async deleteInviteNotification(userId: string, id: string) {
    const notificationsRef = collection(this.firestore, 'users', userId, 'notifications');

    const snapshot = await getDocs(notificationsRef);

    for (const notification of snapshot.docs) {
      const notificationData = notification.data();

      const docPath = notificationData['docPath'].split('/');
      const teamOrEventId = docPath[1];

      let action = '';
      if (docPath[0] === 'teams') action = 'team_invitation'
      else if (docPath[0] === 'events') action = 'event_invitation'

      if (notificationData['action'] === action && teamOrEventId === id) {
        await deleteDoc(notification.ref)
      }
    }
  }

  // apagar notificação de pedido para participar na equipa
  async deleteRequestNotification(teamId: string, userSender: string) {
    try {
      if (!this.userAuthId) return;

      const admins = await this.teamService.getTeamAdmins(teamId);

      for (const adminId of admins) {
        const notificationsRef = collection(this.firestore, 'users', adminId, 'notifications');
        const snapshot = await getDocs(notificationsRef);

        for (const notif of snapshot.docs) {
          const data = notif.data();
          const teamIdFromDoc = (data['docPath'] ?? '').split('/')[1];
          if (data['action'] === 'team_request' && teamIdFromDoc === teamId && data['userSenderId'] === userSender) {
            await deleteDoc(notif.ref);
          }
        }
      }
    } catch { }
  }

}
